import { useMemo, useState } from "react";
import { Checkbox, type Column } from "@legal-500/legal500-design-system";
import { DataTable } from "../../../../components/DataTable/DataTable.js";
import SelectAllCheckbox from "../../../../components/Form/SelectAllCheckbox/SelectAllCheckbox.js";
import {
  GROUP_LABELS,
  GROUPS,
  OUTCOME_LABELS,
  countGroups,
  isSelectable,
  type OutcomeGroup,
  type PreviewRow,
} from "./previewRows.js";
import styles from "./RoleProvisioning.module.css";

/**
 * The dry-run preview (migration-plan.md §6.3): one row per file row, grouped
 * counts above, a group filter, and the selection checkboxes. Which rows CAN be
 * ticked is `isSelectable`'s call; which rows ARE ticked is the tab's state —
 * this component only reports toggles.
 */

type Filter = OutcomeGroup | "all";

type PreviewTableProps = {
  rows: PreviewRow[];
  selected: ReadonlySet<number>;
  includeOwners: boolean;
  disabled: boolean;
  onToggleRow: (lineNumber: number) => void;
  onSetRows: (lineNumbers: number[], isSelected: boolean) => void;
  onIncludeOwnersChange: (include: boolean) => void;
};

const GROUP_PILL: Record<OutcomeGroup, string> = {
  insert: styles.pillOk as string,
  create: styles.pillWarning as string,
  "no-op": styles.pillNeutral as string,
  skipped: styles.pillError as string,
};

function roleList(roles: string[]): string {
  return roles.length === 0 ? "—" : roles.join(", ");
}

export function PreviewTable({
  rows,
  selected,
  includeOwners,
  disabled,
  onToggleRow,
  onSetRows,
  onIncludeOwnersChange,
}: PreviewTableProps) {
  const [filter, setFilter] = useState<Filter>("all");

  const counts = useMemo(() => countGroups(rows), [rows]);
  const ownerRowCount = useMemo(
    () => rows.filter(row => row.outcome === "covered_by_account_owner").length,
    [rows],
  );
  const visibleRows = useMemo(
    () => (filter === "all" ? rows : rows.filter(row => row.group === filter)),
    [rows, filter],
  );

  /** The visible rows the header checkbox governs. */
  const visibleSelectable = useMemo(
    () => visibleRows.filter(row => isSelectable(row.outcome, includeOwners)),
    [visibleRows, includeOwners],
  );
  const visibleSelectedCount = visibleSelectable.filter(row => selected.has(row.lineNumber)).length;
  const allVisibleSelected =
    visibleSelectable.length > 0 && visibleSelectedCount === visibleSelectable.length;

  const columns = useMemo<Column<PreviewRow>[]>(
    () => [
      {
        header: (
          <SelectAllCheckbox
            aria-label="Select all selectable rows shown"
            checked={allVisibleSelected}
            indeterminate={visibleSelectedCount > 0 && !allVisibleSelected}
            disabled={disabled || visibleSelectable.length === 0}
            onChange={() =>
              onSetRows(
                visibleSelectable.map(row => row.lineNumber),
                !allVisibleSelected,
              )
            }
          />
        ) as unknown as string,
        accessor: "id",
        render: (_value, row) =>
          isSelectable(row.outcome, includeOwners) ? (
            <Checkbox
              aria-label={`Select row ${row.lineNumber} — ${row.email}`}
              checked={selected.has(row.lineNumber)}
              disabled={disabled}
              onChange={() => onToggleRow(row.lineNumber)}
            />
          ) : (
            <span className={styles.mono} aria-hidden="true">
              —
            </span>
          ),
      },
      {
        header: "Line",
        accessor: "lineNumber",
        render: (_value, row) => <span className={styles.mono}>{row.lineNumber}</span>,
      },
      {
        header: "Email",
        accessor: "email",
        wrap: true,
        render: (_value, row) => <span className={styles.name}>{row.email || "—"}</span>,
      },
      {
        header: "User",
        accessor: "userName",
        wrap: true,
        render: (_value, row) => (
          <span className={styles.cellStack}>
            <span className={styles.name}>{row.userName ?? "—"}</span>
            {row.userStatus ? (
              <span className={`${styles.pill} ${styles.pillNeutral}`}>{row.userStatus}</span>
            ) : null}
          </span>
        ),
      },
      {
        header: "Organisation",
        accessor: "organisationName",
        wrap: true,
        render: (_value, row) => (
          <span className={styles.cellStack}>
            <span className={styles.name}>{row.organisationName ?? "—"}</span>
            {row.firmRef ? <span className={styles.mono}>firm_ref {row.firmRef}</span> : null}
          </span>
        ),
      },
      {
        header: "Existing roles",
        accessor: "existingRoles",
        wrap: true,
        render: (_value, row) => <span className={styles.mono}>{roleList(row.existingRoles)}</span>,
      },
      {
        header: "Roles to insert",
        accessor: "rolesToInsert",
        wrap: true,
        render: (_value, row) => <span className={styles.mono}>{roleList(row.rolesToInsert)}</span>,
      },
      {
        header: "Outcome",
        accessor: "outcome",
        wrap: true,
        render: (_value, row) => (
          <span className={styles.cellStack}>
            <span className={`${styles.pill} ${GROUP_PILL[row.group]}`}>
              {OUTCOME_LABELS[row.outcome]}
            </span>
            {row.detail ? <span className={styles.rowNote}>{row.detail}</span> : null}
          </span>
        ),
      },
    ],
    [
      allVisibleSelected,
      visibleSelectedCount,
      visibleSelectable,
      selected,
      includeOwners,
      disabled,
      onSetRows,
      onToggleRow,
    ],
  );

  return (
    <div className={styles.stack}>
      <div className={styles.toolbar}>
        <span className={styles.counts} aria-live="polite">
          {GROUPS.map(group => `${GROUP_LABELS[group]} ${counts[group]}`).join(" · ")}
        </span>
        <span className={styles.spacer} />
        <Checkbox
          id="role-provisioning-include-owners"
          label="Also grant to account owners (explicit row for audit)"
          aria-label="Also grant to account owners (explicit row for audit)"
          checked={includeOwners}
          disabled={disabled || ownerRowCount === 0}
          onChange={event => onIncludeOwnersChange(event.target.checked)}
        />
      </div>

      <div className={styles.toolbar} role="group" aria-label="Filter rows by outcome group">
        {(["all", ...GROUPS] as Filter[]).map(option => (
          <button
            key={option}
            type="button"
            className={filter === option ? styles.filterButtonActive : styles.filterButton}
            aria-pressed={filter === option}
            onClick={() => setFilter(option)}
          >
            {option === "all" ? `All ${rows.length}` : `${GROUP_LABELS[option]} ${counts[option]}`}
          </button>
        ))}
      </div>

      <DataTable
        data={visibleRows}
        columns={columns}
        emptyMessage={filter === "all" ? "The scan returned no rows." : "No rows in this group."}
      />
    </div>
  );
}
